import React, { useState, useEffect, useCallback, useRef } from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { useGetProfilesPinDataForDefaultPointQuery, profileApi } from '../../features/profilesSlice';
import { PinChip } from '../MessageProfile';

const MIN_STORY_LENGTH = 100;
const MAX_FETCH_ATTEMPTS = 20;

export interface StoryData {
  id: number;
  name_public: string;
  type: string;
  comment: string;
  formattedsearchterm?: string;
  lat?: number;
  lng?: number;
}

/** Hook: fetches a random patient story once and caches it. Safe to call in a parent that never unmounts. */
export function useRandomPatientStory() {
  const { data: pinData, isSuccess: isPinDataLoaded } = useGetProfilesPinDataForDefaultPointQuery();
  const [fetchProfile] = profileApi.endpoints.getProfile.useLazyQuery();

  const [story, setStory] = useState<StoryData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const triedIds = useRef(new Set<number>());
  const fetching = useRef(false);

  const pickRandomPin = useCallback((pins: any[]): any | null => {
    const untried = pins.filter((p: any) => !triedIds.current.has(p.id));
    if (untried.length === 0) return null;
    return untried[Math.floor(Math.random() * untried.length)];
  }, []);

  useEffect(() => {
    if (!isPinDataLoaded || !pinData?.length || story || fetching.current) return;
    fetching.current = true;

    const findStory = async () => {
      let attempts = 0;
      while (attempts < MAX_FETCH_ATTEMPTS && triedIds.current.size < pinData.length) {
        const pin = pickRandomPin(pinData);
        if (pin === null) break;
        triedIds.current.add(pin.id);
        attempts++;

        try {
          const result = await fetchProfile(pin.id, true).unwrap();
          if (result?.comment && result.comment.length >= MIN_STORY_LENGTH) {
            setStory({
              id: result.id,
              name_public: result.name_public,
              type: result.type,
              comment: result.comment,
              formattedsearchterm: result.formattedsearchterm,
              lat: pin.lat != null ? parseFloat(pin.lat) : undefined,
              lng: pin.lng != null ? parseFloat(pin.lng) : undefined,
            });
            setIsLoading(false);
            return;
          }
        } catch {
          // Skip failed fetches
        }
      }
      setIsLoading(false);
    };

    findStory();
  }, [isPinDataLoaded, pinData, fetchProfile, pickRandomPin, story]);

  return { story, isLoading };
}

interface RandomPatientStoryProps {
  story: StoryData | null;
  isLoading: boolean;
}

export const RandomPatientStory: React.FC<RandomPatientStoryProps> = ({ story, isLoading }) => {
  const { data: profilesPinData } = useGetProfilesPinDataForDefaultPointQuery();
  const totalCount = profilesPinData?.[0]?.count_total;

  return (
    <Box
      sx={{
        borderRadius: 3,
        backgroundColor: 'rgba(244, 240, 255, 0.55)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: 'none',
        boxShadow: '0 2px 12px rgba(80, 60, 160, 0.06), inset 0 0 0 1px rgba(164, 142, 233, 0.18)',
        p: 2.5,
        maxHeight: 320,
        overflowY: 'auto',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(101, 110, 255, 0.3)',
          borderRadius: 2,
        },
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(101, 110, 255, 0.3) transparent',
      }}
    >
      {totalCount && (
        <Typography variant="body2" sx={{ color: '#777', fontSize: 13, mb: 1 }}>
          {totalCount} patients sharing their journeys
        </Typography>
      )}
      {isLoading ? (
        <Stack gap={1.5}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Skeleton animation="wave" variant="rounded" width="40%" height={18} />
            <Skeleton animation="wave" variant="rounded" width={50} height={22} sx={{ borderRadius: 2 }} />
          </Stack>
          <Skeleton animation="wave" variant="rounded" width="100%" height={80} />
          <Skeleton animation="wave" variant="rounded" width="60%" height={14} />
        </Stack>
      ) : story ? (
        <Stack gap={1.5}>
          {/* Name + type badge */}
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>
              {story.name_public}
            </Typography>
            <PinChip
              category="User"
              type={story.type}
              size="small"
              iconSizePx={14}
              sx={{ height: '22px', fontSize: '0.65rem', flexShrink: 0 }}
            />
          </Stack>

          {/* Quote block */}
          <Box
            sx={{
              position: 'relative',
              pl: 2,
              py: 0.5,
              backgroundColor: 'rgba(255, 255, 255, 0.35)',
              borderRadius: 1,
            }}
          >
            <FormatQuoteIcon
              sx={{
                position: 'absolute',
                top: -4,
                left: 4,
                fontSize: 18,
                color: 'rgba(101, 110, 255, 0.3)',
                transform: 'scaleX(-1)',
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: '#444',
                lineHeight: 1.6,
                fontSize: 13,
              }}
            >
              {story.comment}
            </Typography>
          </Box>

          {/* Location if available */}
          {story.formattedsearchterm && (
            <Typography variant="caption" sx={{ color: '#888', fontSize: 11 }}>
              📍 {story.formattedsearchterm}
            </Typography>
          )}
        </Stack>
      ) : (
        <Typography variant="body2" sx={{ color: '#888', textAlign: 'center', py: 2 }}>
          Patient stories will appear here once available.
        </Typography>
      )}
    </Box>
  );
};
